import type { ChangeEvent } from 'react';
import React from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import {
  AdornedInput,
  Alert,
  Box,
  FormField,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Page,
  Radio,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import {
  AuthCheckWrapper,
  HeadWrapper,
  LoadingConstants,
  numberUtils,
  PermissionCheck,
  useLogger,
  useRentalManagerUser,
  useRmwPaths,
} from '@zg-rentals/rental-manager-env';
import { Col, Container, Row } from '@zg-rentals/zrs-comet';

import { INVOICE_PAYMENTS } from '../../constants/RouteConstants';
import BillingConstants from '../../constants/BillingConstants';
import BillingPortalMaintenanceBanner from '../../components/BillingPortalMaintenanceBanner';
import BillingError from '../../components/BillingError';
import BillingPaymentMethods from '../../components/BillingPaymentMethods';
import AutopayStatusWidget from '../../components/AutopayStatusWidget';
import SpinnerContainer from '../../components/SpinnerContainer';
import billingUtils from '../../utils/billingUtils';
import { withErrorBoundary } from '../../components/withErrorBoundry';
import { useInvoice } from '../../hooks/useInvoice';
import { useAppBanner } from '../../hooks/useBanner';
import { emitOtherAmountEvent, EVENT_IDS } from '../../model/ClickstreamEvents';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';
import Cookies from 'js-cookie';
import { BETA_COOKIE } from '@zg-rentals/rental-revenue-components';
import type { AdproductsUserMetroidAlicornPaymentResultDto } from '@zg-rentals/ts-adproducts-client';

const InvoicePaymentPage = () => {
  const match = useRouteMatch();
  const { user } = useRentalManagerUser();
  // @ts-ignore
  const { invoiceId } = match.params;
  const paths = useRmwPaths();
  const [customAmount, setCustomAmount] = React.useState(0);
  const [useBalance, setUseBalance] = React.useState(true);
  const [customAmountErrorMessage, setCustomAmountErrorMessage] = React.useState<string | null>(null);
  const [displaySuccess, setDisplaySuccess] = React.useState(false);
  const [displayFailure, setDisplayFailure] = React.useState(false);
  const [responseCode, setResponseCode] = React.useState(null);

  const [stringEditValue, setStringEditValue] = React.useState('');
  const [isEditingNumberInput, setIsEditingNumberInput] = React.useState(false);
  const history = useHistory();
  const { setBannerState } = useAppBanner();
  const logger = useLogger();

  const {
    invoice: currentInvoice,
    errorMessage: currentInvoiceErrorMessage,
    loadingState: currentInvoiceLoadingState,
    fetchInvoice,
    payInvoice,
    setIsPaymentInProgress,
  } = useInvoice();

  React.useEffect(() => {
    fetchInvoice(invoiceId);
  }, []);

  const handleSetUseBalance = (newValue: boolean) => {
    setUseBalance(newValue);
  };

  const handleSubmit = (selectedCreditCardId: string | undefined | null) => {
    if (!currentInvoice) return Promise.reject();
    if (!selectedCreditCardId) {
      logger.error('Attempting to make payment with no selected credit card');
      return Promise.reject();
    }
    const newErrorMessage = billingUtils.validateInvoicePayment(
      currentInvoice,
      selectedCreditCardId,
      useBalance,
      customAmount,
    );
    if (newErrorMessage) {
      return Promise.reject(new Error(newErrorMessage));
    }
    setIsPaymentInProgress(true);
    setDisplaySuccess(false);
    setDisplayFailure(false);
    setResponseCode(null);
    if (!useBalance) {
      emitOtherAmountEvent({ user });
    }
    return payInvoice({
      invoiceNumber: currentInvoice.invoiceNumber,
      paymentMethodId: selectedCreditCardId,
      amount: useBalance ? currentInvoice.balance : customAmount,
      partialPayment: !useBalance,
    })
      .then((result: AdproductsUserMetroidAlicornPaymentResultDto) => {
        if (result.paymentSuccess) {
          setBannerState({ displaySuccessMessage: true });
          history.push(`${paths.billingPage}/${paths.billingDue}`);
        } else {
          fetchInvoice(invoiceId);
          setDisplayFailure(true);
        }
        return;
      })
      .catch(() => {
        fetchInvoice(invoiceId);
        setDisplayFailure(true);
      });
  };

  const getFormattedCustomAmount = () => {
    const options = {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };
    // @ts-ignore
    return parseFloat(customAmount).toLocaleString(undefined, options);
  };

  const renderPaymentOptions = () => {
    if (!currentInvoice) return;
    return (
      <>
        <FormField>
          <Label required={true}>
            <Heading level={6} css={{ display: 'inline' }}>
              Payment amount:
            </Heading>
          </Label>
          <LabeledControl
            css={{ marginLeft: 'xs' }}
            controlId="LabeledControl-full-balance"
            label={<Label>{`Full Balance of ${numberUtils.asCurrency(currentInvoice.balance)}`}</Label>}
            control={
              <Radio
                name="LabeledControl-full-balance"
                checked={useBalance}
                onChange={() => handleSetUseBalance(true)}
              />
            }
          />
          <Box css={{ marginLeft: 'xs' }}>
            <Box css={{ display: 'flex' }}>
              <LabeledControl
                controlId="LabeledControl-other-amount"
                label={<Label>Other Amount</Label>}
                control={
                  <Radio
                    name="LabeledControl-other-amount"
                    checked={!useBalance}
                    onChange={() => handleSetUseBalance(false)}
                  />
                }
              />
              <Box css={{ marginLeft: 'md' }}>
                <AdornedInput
                  startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                  error={customAmountErrorMessage !== null}
                  input={
                    <AdornedInput.Input
                      value={isEditingNumberInput ? stringEditValue : getFormattedCustomAmount()}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setStringEditValue(value);
                        if (parseFloat(value) > currentInvoice.balance) {
                          setCustomAmountErrorMessage('The amount cannot be greater than the balance.');
                        } else {
                          setCustomAmountErrorMessage(null);
                        }
                      }}
                      onFocus={() => {
                        handleSetUseBalance(false);
                        setIsEditingNumberInput(true);
                      }}
                      onBlur={() => {
                        let numericalValue = parseFloat(parseFloat(stringEditValue).toFixed(2));
                        if (isNaN(numericalValue)) {
                          numericalValue = 0;
                        }
                        setIsEditingNumberInput(false);
                        setCustomAmount(numericalValue);
                        // @ts-ignore
                        setStringEditValue(numericalValue);
                      }}
                    />
                  }
                />
                {customAmountErrorMessage && <FormHelp error={true}>{customAmountErrorMessage}</FormHelp>}
              </Box>
            </Box>
          </Box>
        </FormField>
        <Box css={{ marginBottom: 'sm' }} />
        <BillingPaymentMethods
          accountNumber={currentInvoice.accountNumber}
          onSubmit={handleSubmit}
          submitDisabled={
            isEditingNumberInput ||
            (!useBalance && customAmount === 0) ||
            customAmountErrorMessage !== null ||
            currentInvoice.isPaymentInProgress
          }
          isPaymentInProgress={currentInvoice.isPaymentInProgress}
          isSinglePayment={true}
          autoPayDisabled={!useBalance}
          infoMessage={!useBalance && !customAmount ? 'Please enter an amount.' : ''}
          onSubmitDataEventId={!useBalance ? EVENT_IDS.OTHER_AMOUNT : ''}
        />
      </>
    );
  };

  const renderPaymentActions = () => {
    if (!currentInvoice) return;
    if (currentInvoice.balance > 0) {
      return renderPaymentOptions();
    }
    return (
      <Heading level={6} css={{ textAlign: 'center' }}>
        No Payment Necessary.
      </Heading>
    );
  };

  const formatErrorMessage = () => {
    if (responseCode) {
      const customMessage = BillingConstants.RESPONSE_CODE_MESSAGES[responseCode];
      if (customMessage) return customMessage;
    }
    return 'Payment failed!';
  };

  const renderPaymentSection = () => (
    <Col md={12} lg={5} push={{ sm: 0, lg: 1 }}>
      {displaySuccess && (
        <>
          <Alert tone="success">Thank you for your payment!</Alert>
          <Box css={{ marginBottom: 'sm' }} />
        </>
      )}
      {displayFailure && (
        <>
          <Alert tone="critical">{formatErrorMessage()}</Alert>
          <Box css={{ marginBottom: 'sm' }} />
        </>
      )}
      {renderPaymentActions()}
    </Col>
  );

  const isLoaded = currentInvoiceLoadingState === LoadingConstants.SUCCESS;

  const renderInvoiceDetails = () => {
    if (!currentInvoice) return;
    return (
      <>
        <Box css={{ marginBottom: 'sm' }}>
          <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading level={6}>Account</Heading>
            <Text>{currentInvoice.accountName}</Text>
          </Box>
        </Box>
        <Box css={{ marginBottom: 'sm' }}>
          <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading level={6}>Invoice amount</Heading>
            <Text>{numberUtils.asCurrency(currentInvoice.amount - currentInvoice.taxAmount)}</Text>
          </Box>
        </Box>
        {Cookies.get(BETA_COOKIE) === 'true' && (
          <Box css={{ marginBottom: 'sm' }}>
            <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
              <Heading level={6}>Invoice tax amount</Heading>
              <Text>{numberUtils.asCurrency(currentInvoice.taxAmount)}</Text>
            </Box>
          </Box>
        )}
        <Box css={{ marginBottom: 'sm' }}>
          <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading level={6}>Invoice balance</Heading>
            <Text>{numberUtils.asCurrency(currentInvoice.balance)}</Text>
          </Box>
        </Box>
        <Box css={{ marginBottom: 'sm' }}>
          <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading level={6}>Service period</Heading>
            <Text>{billingUtils.calculateServicePeriod(currentInvoice.zuoraInvoiceItems)}</Text>
          </Box>
        </Box>
        <AutopayStatusWidget accountNumber={currentInvoice.accountNumber} disabled={!isLoaded} />
      </>
    );
  };

  return (
    <AuthCheckWrapper>
      <PermissionCheck route={INVOICE_PAYMENTS}>
        <div className="InvoicePage">
          <HeadWrapper title="Make a payment" />
          <Container>
            <Page.Breadcrumb css={{ marginBottom: 'lg', paddingInline: '24px' }}>
              <TextButton asChild icon={<IconChevronLeftFilled />}>
                <Link
                  to={`/rental-manager/billing/invoice/${invoiceId}`}
                  data-eventid={EVENT_IDS.VIEW_RENTALS_BILLING_INVOICE}
                  data-eventdescription="View billing invoice page event"
                >
                  Back
                </Link>
              </TextButton>
            </Page.Breadcrumb>
            <BillingPortalMaintenanceBanner />
            <Box
              css={{
                background: 'white',
                marginBottom: '20px',
                border: '1px solid',
                borderColor: 'gray.300',
                borderRadius: '5px',
              }}
            >
              <Box css={{ padding: 'lg' }}>
                <Container style={{ padding: 0 }}>
                  <Row hidden={[]}>
                    <Col>
                      <Heading level={4}>Make a payment</Heading>
                      <Box css={{ marginBottom: 'lg' }} />
                      <Heading level={5}>{`#${invoiceId}`}</Heading>
                      <Box css={{ marginBottom: 'md' }} />
                    </Col>
                  </Row>
                  <Row hidden={[]}>
                    <Col sm={12} lg={5}>
                      {isLoaded && renderInvoiceDetails()}
                    </Col>
                    {isLoaded && renderPaymentSection()}
                  </Row>
                </Container>
              </Box>
              {!isLoaded && currentInvoiceLoadingState !== LoadingConstants.ERROR && <SpinnerContainer />}
            </Box>
          </Container>
          {currentInvoiceErrorMessage && (
            <Container>
              <BillingError errorMsg={currentInvoiceErrorMessage} />
            </Container>
          )}
        </div>
      </PermissionCheck>
    </AuthCheckWrapper>
  );
};

export default withErrorBoundary(InvoicePaymentPage);
